import { useEffect } from "react"

import { useGlobalSearchParams, useNavigationContainerRef, useRouter } from "expo-router"

import { REFERRAL_QUERY_PARAM, Referral } from "wealthmode-frontend/util"

// Bounded so a navigator that never mounts leaves the parameter in the URL rather than polling
// forever. 5s at this interval.
const navigatorReadyPollMs = 50
const navigatorReadyMaxAttempts = 100

// The website has no logged-in state, so every visit captures.
export function useReferralCapture() {
  const router = useRouter()
  const params = useGlobalSearchParams()
  const navigationRef = useNavigationContainerRef()
  const code = params[REFERRAL_QUERY_PARAM]

  // Stored before the navigator is ready so a visitor who leaves while fonts and config are
  // still resolving is still credited.
  useEffect(() => {
    if (code === undefined) {
      return
    }

    Referral.captureFromParams(params)
    // params is a fresh object every render; the captured value is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  // Clearing goes through the router rather than history.replaceState, which expo-router would
  // overwrite from its own state on the next render. setParams throws until the navigator has
  // mounted, and this runs from the root layout, which renders a loading state before its Slot —
  // so wait for the same readiness check setParams itself asserts.
  useEffect(() => {
    if (code === undefined) {
      return
    }

    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const clear = () => {
      if (cancelled) {
        return
      }

      if (navigationRef?.isReady()) {
        router.setParams({ [REFERRAL_QUERY_PARAM]: undefined })
        return
      }

      attempts += 1
      if (attempts < navigatorReadyMaxAttempts) {
        timer = setTimeout(clear, navigatorReadyPollMs)
      }
    }

    clear()

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [code, navigationRef, router])
}
